/** The scatter's axis pickers, tick ranges and "which way is better" hint. */

import { Select } from "@/components/ui/select";
import { COST, LATENCY, type Metric } from "./metrics";

const isSpend = (m: Metric) => m === COST || m === LATENCY;

/** A metric's name mid-sentence: "cost per item", but evaluator names as written. */
export function axisName(m: Metric): string {
  return isSpend(m) ? m.label.toLowerCase() : m.label;
}

/** One axis picker: scores, then cost and speed; the other axis's metric is greyed out. */
export function AxisSelect({
  label,
  metrics,
  value,
  other,
  onPick,
}: {
  label: string;
  metrics: Metric[];
  value: string;
  other: string;
  onPick: (key: string) => void;
}) {
  return (
    <Select
      label={label}
      value={value}
      onChange={onPick}
      className="w-[172px]"
      options={metrics.map((m) => ({
        value: m.key,
        label: m.label,
        section: isSpend(m) ? "Cost and speed" : "Scores",
        disabled: m.key === other,
      }))}
    />
  );
}

/** "Best is up and to the left: higher Overall, lower cost per item." */
export function betterHint(y: Metric, x: Metric): string {
  const word = (m: Metric) => `${m.better === "high" ? "higher" : "lower"} ${axisName(m)}`;
  const v = y.better === "high" ? "up" : "down";
  const h = x.better === "high" ? "right" : "left";
  return `Best is ${v} and to the ${h}: ${word(y)}, ${word(x)}.`;
}

/** Scores sit on round steps inside 0–1 (0.80, 0.85, …), with room so the
 *  outermost dot is never on the edge. Cost and latency start at zero. */
export function axisFor(
  metric: Metric,
  values: number[],
): { domain: [number, number]; ticks?: number[]; format: (v: number) => string } {
  if (isSpend(metric)) {
    const max = Math.max(...values, 0);
    return { domain: [0, max > 0 ? max * 1.25 : 1], format: (v) => metric.format(v) };
  }
  const min = Math.min(...values) - 0.02;
  const max = Math.max(...values) + 0.02;
  const step = [0.05, 0.1, 0.2, 0.25].find((s) => (max - min) / s <= 5) ?? 0.25;
  const lo = Math.max(0, Math.floor(min / step) * step);
  const hi = Math.min(1, Math.ceil(max / step) * step);
  const ticks: number[] = [];
  for (let t = lo; t <= hi + 1e-9; t += step) ticks.push(round2(t));
  return { domain: [round2(lo), round2(hi)], ticks, format: (v) => v.toFixed(2) };
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
